import {
  HtmlHTMLAttributes,
  ReactNode,
  useRef,
  useEffect,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  SxProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import {
  Form,
  required,
  useCheckAuth,
  useLogin,
  useNotify,
  useTranslate,
} from "ra-core";
import { BooleanInput, TextInput } from "react-admin";

/**
 * A standalone login page, to serve as authentication gate to the admin
 *
 * Expects the user to enter a login and a password, which will be checked
 * by the `authProvider.login()` method. Redirects to the root page (/)
 * upon success, otherwise displays an authentication error message.
 *
 * Copy and adapt this component to implement your own login logic
 * (e.g. to authenticate via email or facebook or anything else).
 *
 * @example
 *     import MyLoginPage from './MyLoginPage';
 *     const App = () => (
 *         <Admin loginPage={MyLoginPage} authProvider={authProvider}>
 *             ...
 *        </Admin>
 *     );
 */
export const Login = (props: LoginProps) => {
  const { children, backgroundImage, ...rest } = props;
  const containerRef = useRef<any>();
  let backgroundImageLoaded = false;
  const checkAuth = useCheckAuth();
  const navigate = useNavigate();
  useEffect(() => {
    checkAuth({}, false)
      .then(() => {
        // already authenticated, redirect to the home page
        navigate("/");
      })
      .catch(() => {
        // not authenticated, stay on the login page
      });
  }, [checkAuth, navigate]);

  const updateBackgroundImage = () => {
    if (!backgroundImageLoaded && containerRef.current) {
      containerRef.current.style.backgroundImage = `url(${backgroundImage})`;
      backgroundImageLoaded = true;
    }
  };

  // Load background image asynchronously to speed up time to interactive
  const lazyLoadBackgroundImage = () => {
    if (backgroundImage) {
      const img = new Image();
      img.onload = updateBackgroundImage;
      img.src = backgroundImage;
    }
  };

  useEffect(() => {
    if (!backgroundImageLoaded) {
      lazyLoadBackgroundImage();
    }
  });
  return (
    <Root {...rest} ref={containerRef}>
      <Card className={LoginClasses.card}>
        <div className={LoginClasses.avatar}>
          <img height={80} src="/logo.png" alt="ecobebe logo" />
        </div>
        {children}
      </Card>
      <span className={LoginClasses.text}>
        Al usar el servicio aceptas nuestra{" "}
        <Link
          color="inherit"
          href="https://ibericatelemedicina.es/aviso-legal-terminos-y-condiciones/"
          target="_BLANK"
        >
          política de privacidad
        </Link>
      </span>
    </Root>
  );
};

export interface LoginProps extends HtmlHTMLAttributes<HTMLDivElement> {
  backgroundImage?: string;
  children?: ReactNode;
  className?: string;
  sx?: SxProps;
}

const PREFIX = "RaLogin";
export const LoginClasses = {
  card: `${PREFIX}-card`,
  avatar: `${PREFIX}-avatar`,
  icon: `${PREFIX}-icon`,
  text: `${PREFIX}-text`,
};

const Root = styled("div", {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }: any) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  height: "1px",
  alignItems: "center",
  justifyContent: "flex-start",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  backgroundImage:
    "radial-gradient(circle at 50% 14em, #2176ff 0%, #00023b 100%)",

  [`& .${LoginClasses.card}`]: {
    minWidth: 300,
    marginTop: "6em",
  },
  [`& .${LoginClasses.avatar}`]: {
    margin: "1em",
    display: "flex",
    justifyContent: "center",
  },
  [`& .${LoginClasses.icon}`]: {
    backgroundColor: theme.palette.secondary[500],
    width: 80,
    height: 80,
  },
  [`& .${LoginClasses.text}`]: {
    color: "white",
    marginTop: 50,
  },
}));

Login.propTypes = {
  backgroundImage: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
};

export const RememberLoginForm = () => {
  const [loading, setLoading] = useState(false);
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  const submit = (values: Record<string, any>) => {
    setLoading(true);
    login(values)
      .catch((error) => {
        notify(error?.message || "Correo o contraseña incorrectos", {
          type: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <Form onSubmit={submit} mode="onChange" noValidate>
      <CardContent sx={{ width: 300 }}>
        <TextInput
          autoFocus
          source="username"
          label={translate("ra.auth.username")}
          autoComplete="username"
          validate={required()}
          fullWidth
        />
        <TextInput
          source="password"
          label={translate("ra.auth.password")}
          type="password"
          autoComplete="current-password"
          validate={required()}
          fullWidth
        />
        <BooleanInput
          source="rememberMe"
          label="Recuérdame durante 30 días"
          defaultValue={false}
          helperText={false}
        />
        <Button
          variant="contained"
          type="submit"
          color="primary"
          disabled={loading}
          fullWidth
          sx={{ mt: 1 }}
        >
          {loading ? (
            <CircularProgress size={19} thickness={3} color="inherit" />
          ) : (
            translate("ra.auth.sign_in")
          )}
        </Button>
      </CardContent>
    </Form>
  );
};

Login.defaultProps = {
  children: <RememberLoginForm />,
};
